import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Plane, Hotel, MapPin, Calendar, Clock, Users, ChevronDown, ChevronUp,
  Leaf, CheckCircle, Share2, Copy, ArrowLeft, Utensils, Bus, Camera,
  Loader2, AlertCircle, Lock, Sparkles, Download, FileText, Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Event type icons mapping
const eventIcons = {
  flight: Plane,
  hotel: Hotel,
  activity: Camera,
  transfer: Bus,
  meal: Utensils,
  default: MapPin
};

// Event type colors
const eventColors = {
  flight: 'bg-blue-500',
  hotel: 'bg-purple-500',
  activity: 'bg-green-500',
  transfer: 'bg-orange-500',
  meal: 'bg-pink-500',
  default: 'bg-gray-500'
};

const ItineraryViewer = () => {
  const { token } = useParams(); // Can be booking_id or magic_link_token
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [magicLink, setMagicLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  useEffect(() => {
    fetchItinerary();
  }, [token]);

  const fetchItinerary = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      // Check if this is a magic link (ends with _hash where hash is 24 chars) or booking_id
      // Magic link format: bookingId_hash (e.g., book_test_4febc3b0_aa283b33d7a56b8d1100aa25)
      const parts = token.split('_');
      const lastPart = parts[parts.length - 1];
      const isMagicLink = lastPart && lastPart.length === 24 && /^[a-f0-9]+$/.test(lastPart);
      
      if (isMagicLink) {
        // Magic link - no auth required
        response = await axios.get(`${API_URL}/itinerary/magic/${token}`);
      } else {
        // Regular booking ID - requires auth
        response = await axios.get(`${API_URL}/itinerary/booking/${token}`, {
          withCredentials: true
        });
      }
      
      setData(response.data);
      
      // Auto-expand first day
      if (response.data.itinerary?.days?.length > 0) {
        setExpandedDays({ 1: true });
      }
      
      // Set magic link if available
      if (response.data.booking?.magic_link) {
        setMagicLink(response.data.booking.magic_link);
      }
      
    } catch (err) {
      console.error('Error fetching itinerary:', err);
      if (err.response?.status === 403) {
        setError({
          type: 'payment_required',
          message: err.response.data?.detail || 'Payment required to view itinerary'
        });
      } else if (err.response?.status === 404) {
        setError({
          type: 'not_found',
          message: 'Itinerary not found'
        });
      } else {
        setError({
          type: 'error',
          message: err.response?.data?.detail || 'Failed to load itinerary'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (dayNumber) => {
    setExpandedDays(prev => ({
      ...prev,
      [dayNumber]: !prev[dayNumber]
    }));
  };

  const generateMagicLink = async () => {
    if (!data?.booking?.booking_id) return;
    
    setGeneratingLink(true);
    try {
      const response = await axios.post(
        `${API_URL}/itinerary/magic-link/${data.booking.booking_id}`,
        {},
        { withCredentials: true }
      );
      setMagicLink(response.data.magic_link);
      toast.success('Magic link generated!');
    } catch (err) {
      toast.error('Failed to generate magic link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyMagicLink = () => {
    if (magicLink) {
      navigator.clipboard.writeText(magicLink);
      toast.success('Link copied to clipboard!');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#f5f0e8] to-[#faf8f5]">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#84bc00] animate-spin mx-auto mb-4" />
          <p className="text-[#3d3d3d] font-semibold">Loading your itinerary...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#f5f0e8] to-[#faf8f5] px-4">
        <Card className="max-w-md w-full shadow-lg">
          <CardContent className="pt-8 text-center">
            {error.type === 'payment_required' ? (
              <>
                <Lock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                <h2 className="text-2xl font-black mb-2">Payment Required</h2>
                <p className="text-gray-600 mb-6">{error.message}</p>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-[#84bc00] hover:bg-[#6a9600]"
                >
                  Go to Dashboard
                </Button>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-black mb-2">Oops!</h2>
                <p className="text-gray-600 mb-6">{error.message}</p>
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                >
                  Go Home
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { booking, itinerary, eco_impact } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f0e8] via-[#faf8f5] to-[#f5f0e8]">
      {/* Hero Header */}
      <div 
        className="relative h-64 md:h-80 bg-cover bg-center"
        style={{ 
          backgroundImage: itinerary.image 
            ? `url(${itinerary.image})` 
            : 'linear-gradient(135deg, #84bc00 0%, #5a8a00 100%)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-[#f5f0e8]" />
        
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors shadow-md"
        >
          <ArrowLeft className="w-6 h-6 text-[#3d5a00]" />
        </button>

        {/* Share Button */}
        {!data.is_magic_link && (
          <button 
            onClick={magicLink ? copyMagicLink : generateMagicLink}
            disabled={generatingLink}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors shadow-md"
          >
            {generatingLink ? (
              <Loader2 className="w-6 h-6 text-[#3d5a00] animate-spin" />
            ) : magicLink ? (
              <Copy className="w-6 h-6 text-[#3d5a00]" />
            ) : (
              <Share2 className="w-6 h-6 text-[#3d5a00]" />
            )}
          </button>
        )}

        {/* Download Button */}
        <div className="absolute top-4 right-16 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white transition-colors shadow-md">
                <Download className="w-6 h-6 text-[#3d5a00]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>English (EN)</DropdownMenuLabel>
              <DropdownMenuItem 
                onClick={() => window.open(`${API_URL}/itinerary/pdf/invoice/${booking.booking_id}?lang=en`, '_blank')}
              >
                <Receipt size={16} className="mr-2" />
                Invoice (EN)
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => window.open(`${API_URL}/itinerary/pdf/confirmation/${booking.booking_id}?lang=en`, '_blank')}
              >
                <FileText size={16} className="mr-2" />
                Booking Confirmation (EN)
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => window.open(`${API_URL}/itinerary/pdf/itinerary/${booking.booking_id}?lang=en`, '_blank')}
              >
                <Calendar size={16} className="mr-2" />
                Full Itinerary (EN)
              </DropdownMenuItem>
              
              <DropdownMenuSeparator />
              
              <DropdownMenuLabel>Español (ES)</DropdownMenuLabel>
              <DropdownMenuItem 
                onClick={() => window.open(`${API_URL}/itinerary/pdf/invoice/${booking.booking_id}?lang=es`, '_blank')}
              >
                <Receipt size={16} className="mr-2" />
                Factura (ES)
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => window.open(`${API_URL}/itinerary/pdf/confirmation/${booking.booking_id}?lang=es`, '_blank')}
              >
                <FileText size={16} className="mr-2" />
                Confirmación (ES)
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => window.open(`${API_URL}/itinerary/pdf/itinerary/${booking.booking_id}?lang=es`, '_blank')}
              >
                <Calendar size={16} className="mr-2" />
                Itinerario (ES)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Trip Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          <Badge className="bg-[#84bc00] text-black font-bold mb-2">
            {itinerary.burn_label || 'Adventure'}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-black mb-2">
            {itinerary.trip_name}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm opacity-90">
            <span className="flex items-center gap-1">
              <MapPin size={16} />
              {itinerary.destination}
            </span>
            <span className="flex items-center gap-1">
              <Calendar size={16} />
              {itinerary.duration}
            </span>
            <span className="flex items-center gap-1">
              <Users size={16} />
              {booking.travelers} traveler{booking.travelers > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        
        {/* Booking Status Card */}
        <Card className="mb-8 border-2 border-[#84bc00]/30 bg-white/90 backdrop-blur shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#8b7355] text-sm">Booking Status</p>
                <p className="text-[#3d3d3d] font-bold text-lg">
                  {booking.status === 'paid_in_full' ? 'Fully Paid' : 
                   booking.status === 'deposit_paid' ? 'Deposit Paid' : 
                   booking.status}
                </p>
              </div>
              <Badge 
                className={
                  booking.status === 'paid_in_full' 
                    ? 'bg-[#84bc00] text-black' 
                    : 'bg-orange-500 text-white'
                }
              >
                {booking.status === 'paid_in_full' ? 'CONFIRMED' : 'PENDING BALANCE'}
              </Badge>
            </div>
            
            {booking.balance_due > 0 && (
              <div className="mt-4 pt-4 border-t border-[#e8e0d5]">
                <p className="text-sm text-[#8b7355]">Balance Due</p>
                <p className="text-2xl font-black text-orange-600">
                  ${booking.balance_due.toLocaleString()}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Day-by-Day Timeline */}
        <div className="mb-8">
          <h2 className="text-2xl font-black text-[#3d3d3d] mb-6 flex items-center gap-2">
            <Calendar className="text-[#84bc00]" />
            Your Journey
          </h2>
          
          <div className="space-y-4">
            {itinerary.days && itinerary.days.length > 0 ? (
              itinerary.days.map((day, index) => {
                const isExpanded = expandedDays[day.day_number];
                
                return (
                  <div 
                    key={day.day_number}
                    className="relative"
                  >
                    {/* Timeline connector */}
                    {index < itinerary.days.length - 1 && (
                      <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-gradient-to-b from-[#84bc00] to-[#d4c4a8]" />
                    )}
                    
                    {/* Day Card */}
                    <Card 
                      className={`
                        border-2 transition-all duration-300 cursor-pointer shadow-md
                        ${isExpanded 
                          ? 'border-[#84bc00] bg-white' 
                          : 'border-[#e8e0d5] bg-white/80 hover:border-[#84bc00]/50'
                        }
                      `}
                      onClick={() => toggleDay(day.day_number)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            {/* Day Number Badge */}
                            <div className="w-12 h-12 rounded-full bg-[#84bc00] flex items-center justify-center font-black text-white text-lg shadow-md">
                              {day.day_number}
                            </div>
                            <div>
                              <CardTitle className="text-[#3d3d3d] text-lg">
                                {day.title || `Day ${day.day_number}`}
                              </CardTitle>
                              {day.date && (
                                <p className="text-sm text-[#8b7355]">{day.date}</p>
                              )}
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="text-[#84bc00]" />
                          ) : (
                            <ChevronDown className="text-[#8b7355]" />
                          )}
                        </div>
                      </CardHeader>
                      
                      {/* Expanded Content */}
                      {isExpanded && (
                        <CardContent className="pt-4 border-t border-[#e8e0d5]">
                          {day.description && (
                            <p className="text-[#5a5a5a] mb-4">{day.description}</p>
                          )}
                          
                          {/* Events Timeline */}
                          {day.events && day.events.length > 0 && (
                            <div className="space-y-3">
                              {day.events.map((event, eventIndex) => {
                                const IconComponent = eventIcons[event.type] || eventIcons.default;
                                const colorClass = eventColors[event.type] || eventColors.default;
                                
                                return (
                                  <div 
                                    key={eventIndex}
                                    className="flex items-start gap-3 p-3 rounded-lg bg-[#faf8f5] border border-[#e8e0d5]"
                                  >
                                    <div className={`p-2 rounded-lg ${colorClass}`}>
                                      <IconComponent size={18} className="text-white" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center justify-between">
                                        <p className="font-bold text-[#3d3d3d]">{event.title}</p>
                                        {event.time && (
                                          <span className="text-xs text-[#8b7355] flex items-center gap-1">
                                            <Clock size={12} />
                                            {event.time}
                                          </span>
                                        )}
                                      </div>
                                      {event.description && (
                                        <p className="text-sm text-[#6b6b6b] mt-1">
                                          {event.description}
                                        </p>
                                      )}
                                      {event.location && (
                                        <p className="text-xs text-[#84bc00] mt-1 flex items-center gap-1">
                                          <MapPin size={12} />
                                          {event.location}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          
                          {/* Meals Included */}
                          {day.meals_included && day.meals_included.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-[#e8e0d5]">
                              <p className="text-sm text-[#8b7355] mb-2">Meals Included:</p>
                              <div className="flex gap-2">
                                {day.meals_included.map((meal, i) => (
                                  <Badge key={i} variant="outline" className="text-[#84bc00] border-[#84bc00] bg-[#84bc00]/10">
                                    {meal}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Accommodation */}
                          {day.accommodation && (
                            <div className="mt-4 pt-4 border-t border-[#e8e0d5] flex items-center gap-2">
                              <Hotel size={16} className="text-purple-500" />
                              <span className="text-sm text-[#5a5a5a]">{day.accommodation}</span>
                            </div>
                          )}
                        </CardContent>
                      )}
                    </Card>
                  </div>
                );
              })
            ) : (
              <Card className="border-[#e8e0d5] bg-white/80">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-[#d4c4a8] mx-auto mb-4" />
                  <p className="text-[#6b6b6b]">
                    Detailed itinerary coming soon!
                  </p>
                  <p className="text-sm text-[#8b7355] mt-2">
                    Your travel consultant is preparing your day-by-day schedule.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* What's Included */}
        {itinerary.included && itinerary.included.length > 0 && (
          <Card className="mb-8 border-[#e8e0d5] bg-white/90 shadow-md">
            <CardHeader>
              <CardTitle className="text-[#3d3d3d] flex items-center gap-2">
                <CheckCircle className="text-[#84bc00]" />
                What's Included
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {itinerary.included.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#5a5a5a]">
                    <CheckCircle size={16} className="text-[#84bc00] mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Sustainable Impact Section */}
        {eco_impact && (eco_impact.receipts?.length > 0 || eco_impact.eco_energy || eco_impact.eco_staff || eco_impact.eco_waste) && (
          <Card className="border-2 border-[#84bc00]/30 bg-gradient-to-br from-[#f0f7e6] to-white shadow-md">
            <CardHeader>
              <CardTitle className="text-[#3d5a00] flex items-center gap-2">
                <Leaf className="text-[#84bc00]" />
                Your Sustainable Impact
              </CardTitle>
            </CardHeader>
            <CardContent>
              {eco_impact.intro && (
                <p className="text-[#5a5a5a] mb-6">{eco_impact.intro}</p>
              )}
              
              {/* Eco Badges */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {eco_impact.eco_energy && (
                  <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                    <Sparkles className="text-green-600 mb-2" size={24} />
                    <p className="text-sm font-bold text-green-700">Clean Energy</p>
                    <p className="text-xs text-green-600 mt-1">{eco_impact.eco_energy}</p>
                  </div>
                )}
                {eco_impact.eco_staff && (
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <Users className="text-blue-600 mb-2" size={24} />
                    <p className="text-sm font-bold text-blue-700">Local Impact</p>
                    <p className="text-xs text-blue-600 mt-1">{eco_impact.eco_staff}</p>
                  </div>
                )}
                {eco_impact.eco_waste && (
                  <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                    <Leaf className="text-purple-600 mb-2" size={24} />
                    <p className="text-sm font-bold text-purple-700">Zero Waste</p>
                    <p className="text-xs text-purple-600 mt-1">{eco_impact.eco_waste}</p>
                  </div>
                )}
              </div>

              {/* Eco Receipts List */}
              {eco_impact.receipts && eco_impact.receipts.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-bold text-[#3d5a00] mb-3">See The Receipts:</p>
                  {eco_impact.receipts.map((receipt, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-3 p-3 rounded-lg bg-[#faf8f5] border border-[#e8e0d5]"
                    >
                      <CheckCircle size={18} className="text-[#84bc00] flex-shrink-0" />
                      <span className="text-sm text-[#5a5a5a]">{receipt}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Verified By */}
              {eco_impact.verified_by && eco_impact.verified_by.length > 0 && (
                <div className="mt-6 pt-6 border-t border-[#e8e0d5]">
                  <p className="text-xs text-[#8b7355] mb-2">Verified by:</p>
                  <div className="flex flex-wrap gap-2">
                    {eco_impact.verified_by.map((partner, i) => (
                      <Badge 
                        key={i} 
                        variant="outline" 
                        className="text-[#6b6b6b] border-[#d4c4a8] bg-white"
                      >
                        {partner}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Magic Link Share Section */}
        {!data.is_magic_link && (
          <Card className="mt-8 border-[#e8e0d5] bg-white/90 shadow-md">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-[#3d3d3d] font-bold">Share & Download</p>
                  <p className="text-sm text-[#8b7355]">
                    Share with travel companions or download documents
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {/* Download Dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="border-[#e8e0d5] text-[#3d3d3d] hover:border-[#84bc00]">
                        <Download size={16} className="mr-2" />
                        Download
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>English (EN)</DropdownMenuLabel>
                      <DropdownMenuItem 
                        onClick={() => window.open(`${API_URL}/itinerary/pdf/invoice/${booking.booking_id}?lang=en`, '_blank')}
                      >
                        <Receipt size={16} className="mr-2" />
                        Invoice (EN)
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => window.open(`${API_URL}/itinerary/pdf/confirmation/${booking.booking_id}?lang=en`, '_blank')}
                      >
                        <FileText size={16} className="mr-2" />
                        Booking Confirmation (EN)
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => window.open(`${API_URL}/itinerary/pdf/itinerary/${booking.booking_id}?lang=en`, '_blank')}
                      >
                        <Calendar size={16} className="mr-2" />
                        Full Itinerary (EN)
                      </DropdownMenuItem>
                      
                      <DropdownMenuSeparator />
                      
                      <DropdownMenuLabel>Español (ES)</DropdownMenuLabel>
                      <DropdownMenuItem 
                        onClick={() => window.open(`${API_URL}/itinerary/pdf/invoice/${booking.booking_id}?lang=es`, '_blank')}
                      >
                        <Receipt size={16} className="mr-2" />
                        Factura (ES)
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => window.open(`${API_URL}/itinerary/pdf/confirmation/${booking.booking_id}?lang=es`, '_blank')}
                      >
                        <FileText size={16} className="mr-2" />
                        Confirmación (ES)
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => window.open(`${API_URL}/itinerary/pdf/itinerary/${booking.booking_id}?lang=es`, '_blank')}
                      >
                        <Calendar size={16} className="mr-2" />
                        Itinerario (ES)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Share Link Button */}
                  {magicLink ? (
                    <Button
                      onClick={copyMagicLink}
                      variant="outline"
                      className="border-[#84bc00] text-[#3d5a00] hover:bg-[#84bc00] hover:text-white"
                    >
                      <Copy size={16} className="mr-2" />
                      Copy Link
                    </Button>
                  ) : (
                    <Button
                      onClick={generateMagicLink}
                      disabled={generatingLink}
                      className="bg-[#84bc00] hover:bg-[#6a9600] text-white"
                    >
                      {generatingLink ? (
                        <Loader2 size={16} className="mr-2 animate-spin" />
                      ) : (
                        <Share2 size={16} className="mr-2" />
                      )}
                      Generate Link
                    </Button>
                  )}
                </div>
              </div>
              
              {magicLink && (
                <div className="mt-4 p-3 rounded-lg bg-[#faf8f5] border border-[#e8e0d5] flex items-center gap-2">
                  <input
                    type="text"
                    value={magicLink}
                    readOnly
                    className="flex-1 bg-transparent text-sm text-[#5a5a5a] outline-none"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyMagicLink}
                    className="text-[#84bc00] hover:text-[#3d5a00]"
                  >
                    <Copy size={14} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ItineraryViewer;
